'use client';

/**
 * global-error.tsx
 *
 * Root-level error boundary (wraps the root layout itself). This is the
 * last line of defense for errors that occur during root-layout rendering —
 * most critically ChunkLoadError, which fires when a mid-session deployment
 * has replaced the JS bundles the browser is holding in memory.
 *
 * ChunkLoadError recovery strategy:
 *   1. On first chunk error (or after a 15 s gap since the last attempt):
 *      store a timestamp in sessionStorage, then reload. The fresh HTML
 *      references the current chunk hashes → page loads cleanly.
 *   2. If a reload was already attempted within the last 15 s: the chunk is
 *      genuinely missing — show a manual-reload UI so the user is not trapped
 *      in an infinite reload loop. Using a TTL instead of a permanent flag
 *      ensures a second deployment later in the same tab also gets an
 *      automatic retry.
 *
 * All other errors fall through to the generic 500 UI.
 *
 * Note: Next.js requires global-error to supply its own <html>/<body> tags
 * because it replaces the root layout entirely.
 */

import { useEffect, useState } from 'react';

const CHUNK_RELOAD_FLAG = 'dc.chunkReloadAttempted';
const CHUNK_RELOAD_TTL_MS = 15_000;

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /loading chunk \d+ failed/i.test(error.message) ||
    /loading css chunk \d+ failed/i.test(error.message) ||
    /failed to fetch dynamically imported module/i.test(error.message)
  );
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isChunk = isChunkLoadError(error);
  const [reloadBlocked, setReloadBlocked] = useState(false);

  useEffect(() => {
    if (!isChunk) return;

    try {
      const storedAt = sessionStorage.getItem(CHUNK_RELOAD_FLAG);
      const isRecent =
        storedAt !== null && Date.now() - Number(storedAt) < CHUNK_RELOAD_TTL_MS;
      if (!isRecent) {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
        window.location.reload();
        return;
      }
    } catch {
      // sessionStorage unavailable — fall through to manual UI
    }

    setReloadBlocked(true);
  }, [isChunk]);

  const handleManualReload = () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    } catch {}
    window.location.reload();
  };

  return (
    <html lang="en">
      <body
        style={{
          backgroundColor: '#111827',
          color: '#f9fafb',
          fontFamily:
            'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          margin: 0,
          padding: 0,
        }}
      >
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '1.5rem',
          }}
        >
          <div
            style={{ maxWidth: '28rem', textAlign: 'center', width: '100%' }}
          >
            {isChunk && !reloadBlocked ? (
              <>
                <div style={{ fontSize: '3.75rem', marginBottom: '1.5rem' }}>
                  🔄
                </div>
                <h1
                  style={{
                    fontSize: '1.875rem',
                    fontWeight: 700,
                    marginBottom: '0.75rem',
                  }}
                >
                  Updating Dream Connect…
                </h1>
                <p
                  style={{
                    color: '#9ca3af',
                    fontSize: '0.875rem',
                    lineHeight: '1.625',
                    marginBottom: '1.5rem',
                  }}
                >
                  A new version of Dream Connect was deployed. The page is
                  reloading automatically to pick up the latest build.
                </p>
                <div
                  style={{
                    animation: 'spin 1s linear infinite',
                    borderColor: '#6366f1 transparent transparent transparent',
                    borderRadius: '50%',
                    borderStyle: 'solid',
                    borderWidth: '3px',
                    display: 'inline-block',
                    height: '2rem',
                    width: '2rem',
                  }}
                />
              </>
            ) : isChunk && reloadBlocked ? (
              <>
                <div style={{ fontSize: '3.75rem', marginBottom: '1.5rem' }}>
                  ⚠️
                </div>
                <h1
                  style={{
                    fontSize: '1.875rem',
                    fontWeight: 700,
                    marginBottom: '0.75rem',
                  }}
                >
                  Could not load Dream Connect
                </h1>
                <p
                  style={{
                    color: '#9ca3af',
                    fontSize: '0.875rem',
                    lineHeight: '1.625',
                    marginBottom: '1.5rem',
                  }}
                >
                  A required asset could not be loaded. Please try a manual
                  reload. If the problem persists, clear your browser cache or
                  contact support.
                </p>
                <button
                  onClick={handleManualReload}
                  style={{
                    backgroundColor: '#4f46e5',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    marginRight: '0.75rem',
                    padding: '0.625rem 1.25rem',
                  }}
                  type="button"
                >
                  Reload page
                </button>
              </>
            ) : (
              <>
                <h1
                  style={{
                    fontSize: '4.5rem',
                    fontWeight: 800,
                    marginBottom: '1rem',
                  }}
                >
                  500
                </h1>
                <p
                  style={{
                    fontSize: '1.875rem',
                    fontWeight: 700,
                    marginBottom: '1rem',
                  }}
                >
                  Something went wrong!
                </p>
                <p
                  style={{
                    color: '#9ca3af',
                    fontSize: '1rem',
                    marginBottom: '1.5rem',
                  }}
                >
                  We are sorry for the inconvenience. Please try again later.
                </p>
                <button
                  onClick={reset}
                  style={{
                    backgroundColor: '#7c3aed',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginRight: '0.75rem',
                    padding: '0.625rem 1.25rem',
                  }}
                  type="button"
                >
                  Try again
                </button>
              </>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}
